import React, { FC, useEffect, useMemo, useState } from 'react';
import { AutoComplete, Button, Checkbox, Drawer, Form, Input, Select, TimePicker } from 'antd';
import type { RangeValue } from 'rc-picker/lib/interface';
import { useForm } from 'antd/es/form/Form';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import dayjs, { Dayjs } from 'dayjs';
import { WarningCard } from 'common/components/WarningCard';
import { EPlaceholder } from 'common/const/placeholder.enum';
import { debounce } from 'common/helpers/common.helper';
import { rules } from 'common/helpers/form.helper';
import { SELECT_LIST_HEIGHT_320 } from 'common/config';
import { IFormValue } from 'common/models';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as CloseIcon } from 'app/assets/images/redesign/close.svg';
import { getAddressSuggestionOptions } from 'entities/Address/Address.helper';
import {
  EDayOfWeek,
  EDayOfWeekRu,
  initialStockSchedules,
  IStock,
  IStockSchedule,
  IUpdateStockPayload,
} from 'entities/Stock/Stock.models';
import { getAddressSuggestions, selectAddressSuggestions, setAddressSuggestions } from 'entities/Address/Address.slice';
import { getCityList, selectCityList } from 'entities/City/City.slice';

interface IComponentProps {
  open: boolean;
  loading: boolean;
  stock: IStock | null;
  onCancel: () => void;
  onSubmit: (payload: Omit<IUpdateStockPayload, 'id'>) => void;
}

export const StockCreateCard: FC<IComponentProps> = ({ open, loading, stock, onCancel, onSubmit }) => {
  const dispatch = useAppDispatch();
  // address
  const addressSuggestions = useAppSelector(selectAddressSuggestions);
  // city
  const cityList = useAppSelector(selectCityList);
  const cityOptions = useMemo(() => cityList.map((item) => ({ value: item.id, label: item.name })), [cityList]);

  const [payload, setPayload] = useState<Omit<IUpdateStockPayload, 'id'>>({});
  const [formNotSaved, setFormNotSaved] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [showSchedules, setShowSchedules] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<IStockSchedule[]>(initialStockSchedules);
  const [form] = useForm();

  useEffect(() => {
    if (open) {
      dispatch(getCityList({ limit: 0 }));
    }
  }, [open]);

  useEffect(() => {
    if (open && stock) {
      form.setFieldsValue(stock);

      if (stock.schedules?.length) {
        setShowSchedules(true);
        setSchedules(stock.schedules);
      }
    }
  }, [open, stock]);

  const addressSuggestionOptions = getAddressSuggestionOptions(addressSuggestions);
  const submitDisabled = stock ? !formNotSaved : false;

  const debouncedSearch = debounce((value: string) => dispatch(getAddressSuggestions({ name: value })));

  const selectAddress = (value: string): void => {
    const address = addressSuggestions.find((item) => item.name === value);

    if (address) {
      setFormNotSaved(true);

      // Город из подсказки приходит строкой — резолвим в cityId по справочнику (best-effort).
      const matchedCity = address.city
        ? cityList.find((item) => item.name.toLowerCase() === address.city?.toLowerCase())
        : undefined;

      form.setFieldsValue({
        address: address.name,
        ...(matchedCity ? { cityId: matchedCity.id } : {}),
      });

      if (stock) {
        setPayload((prev) => ({
          ...prev,
          address: address.name,
          ...(matchedCity ? { cityId: matchedCity.id } : {}),
        }));
      }
    }
  };

  const onFormValuesChange = (value: IFormValue): void => {
    setFormNotSaved(true);
    setPayload((prev) => ({ ...prev, ...value }));
  };

  const closeWarningCard = (): void => {
    setOpenWarningCard(false);
  };

  const toggleShowSchedules = (e: CheckboxChangeEvent): void => {
    setFormNotSaved(true);

    if (stock?.schedules?.length) {
      setSchedules(stock.schedules);
    } else {
      setSchedules(initialStockSchedules);
    }

    setShowSchedules(e.target.checked);
  };

  const changeTime = (dayOfWeek: string, value: RangeValue<Dayjs>): void => {
    const openTime = value ? dayjs(value[0]).format('HH:mm') : undefined;
    const closeTime = value ? dayjs(value[1]).format('HH:mm') : undefined;

    setFormNotSaved(true);

    setSchedules((prev) =>
      prev.map((item) => {
        if (item.dayOfWeek === dayOfWeek) {
          return {
            ...item,
            openTime,
            closeTime,
          };
        }

        return item;
      }),
    );
  };

  const changeIsDayOff = (dayOfWeek: string, value: boolean): void => {
    setFormNotSaved(true);
    setSchedules((prev) =>
      prev.map((item) => {
        if (item.dayOfWeek === dayOfWeek) {
          return {
            ...item,
            isDayOff: value,
            openTime: value ? undefined : item.openTime,
            closeTime: value ? undefined : item.closeTime,
            isError: false,
          };
        }

        return item;
      }),
    );
  };

  const cancel = (): void => {
    dispatch(setAddressSuggestions([]));
    setOpenWarningCard(false);
    setFormNotSaved(false);
    setPayload({});
    form.resetFields();
    setShowSchedules(false);
    setSchedules(initialStockSchedules);
    onCancel();
  };

  const handleCancel = (): void => {
    if (loading) {
      return;
    }

    if (formNotSaved) {
      setOpenWarningCard(true);
      return;
    }

    cancel();
  };

  const handleSubmit = ({ name, address, cityId, isCentral }: IUpdateStockPayload): void => {
    if (showSchedules) {
      const checkedSchedules = schedules.map((item) => {
        return {
          ...item,
          isError: !item.isDayOff && (!item.openTime || !item.closeTime),
        };
      });

      if (checkedSchedules.some((item) => item.isError)) {
        setSchedules(checkedSchedules);
        return;
      }
    }

    if (stock) {
      // eslint-disable-next-line
      const schedulesPayload = schedules.map(({ isError, ...item }) => ({ ...item }));

      onSubmit({
        ...payload,
        address: payload.address?.replaceAll('\n', ' '),
        schedules: showSchedules ? schedulesPayload : [],
        onSuccess: cancel,
      });
    } else {
      onSubmit({
        name,
        address: (address as string).replaceAll('\n', ' '),
        cityId,
        isCentral,
        schedules,
        onSuccess: cancel,
      });
    }
  };

  return (
    <>
      <Drawer
        rootClassName="redesign drawer stock-create-card"
        destroyOnClose
        width={720}
        open={open}
        onClose={handleCancel}
        footer={
          <Button className="button-lg primary" onClick={form.submit} loading={loading} disabled={submitDisabled}>
            {stock ? 'Сохранить изменения' : 'Добавить склад'}
          </Button>
        }
      >
        <div className="drawer__body">
          <div className="drawer__title mb-14">{stock ? 'Редактировать склад' : 'Новый склад отгрузки'}</div>

          <Form form={form} layout="vertical" requiredMark={false} onValuesChange={onFormValuesChange} onFinish={handleSubmit}>
            <Form.Item className="mb-9" label="Название склада*" name="name" rules={[rules.required()]}>
              <Input
                placeholder={EPlaceholder.EnterName}
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
              />
            </Form.Item>

            <div className="input-with-label mb-9">
              <AutoComplete
                rootClassName="redesign"
                popupClassName="redesign"
                className="redesign autosize-autocomplete"
                virtual={false}
                listHeight={SELECT_LIST_HEIGHT_320}
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
                onChange={debouncedSearch}
                options={addressSuggestionOptions}
                onSelect={selectAddress}
              >
                <Input.TextArea placeholder={EPlaceholder.StartTypingAddress} autoSize />
              </AutoComplete>

              <div className="text-tag mb-3 input-label">Автоматический поиск</div>
            </div>

            <Form.Item className="mb-9" label="Адрес*" name="address" rules={[rules.required()]}>
              <Input.TextArea
                className="autosize-input"
                placeholder={EPlaceholder.SpecifyAddress}
                autoSize
                allowClear={{ clearIcon: <CloseIcon className="icon-close-input" /> }}
              />
            </Form.Item>

            <Form.Item className="mb-9" label="Город*" name="cityId" rules={[rules.required()]}>
              <Select
                rootClassName="redesign"
                popupClassName="redesign"
                className="redesign"
                placeholder={EPlaceholder.SpecifyCity}
                showSearch
                allowClear
                listHeight={SELECT_LIST_HEIGHT_320}
                options={cityOptions}
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>

            <Form.Item className="mb-9" name="isCentral" valuePropName="checked">
              <Checkbox>
                <div className="ff-regular fs-s">Центральный склад (виден из всех городов)</div>
              </Checkbox>
            </Form.Item>
          </Form>

          <div>
            <div className="mb-6 ">
              <Checkbox checked={showSchedules} onChange={toggleShowSchedules}>
                <div className="ff-regular fs-s">Указать время работы</div>
              </Checkbox>
            </div>

            {showSchedules && (
              <div>
                {schedules.map((item) => {
                  const openTime = item.openTime ? dayjs(item.openTime, 'HH:mm') : null;
                  const closeTime = item.closeTime ? dayjs(item.closeTime, 'HH:mm') : null;

                  return (
                    <div
                      key={item.dayOfWeek}
                      style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}
                      className="mb-4 align-center"
                    >
                      <span className="ff-regular fs-s">{EDayOfWeekRu[EDayOfWeek[item.dayOfWeek]]}</span>

                      <div>
                        <TimePicker.RangePicker
                          format="HH:mm"
                          placeholder={['с', 'по']}
                          suffixIcon={false}
                          disabled={item.isDayOff}
                          value={[openTime, closeTime]}
                          onChange={(value) => changeTime(item.dayOfWeek, value)}
                          status={item.isError ? 'error' : undefined}
                        />

                        {item.isError && <span className="ff-regular fs-xs color-red">Обязательное поле</span>}
                      </div>

                      <div className="flex justify-end">
                        <Checkbox checked={item.isDayOff} onChange={(e) => changeIsDayOff(item.dayOfWeek, e.target.checked)}>
                          <div className="ff-regular fs-s">Выходной</div>
                        </Checkbox>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Drawer>

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что не хотите сохранить изменения?"
        subtitle="Введенные данные будут потеряны."
        confirmBtnTitle="Не сохранять"
        onConfirm={cancel}
        onClose={closeWarningCard}
      />
    </>
  );
};
